import { capitalize, every, isEmpty, map, pickBy } from "lodash";
import React from "react";
import { StyleSheet } from "react-native";
import { useSelector } from "react-redux";

import { tColumn } from "src/hooks/table";
import { kStyleConst } from "src/kStyle";
import { useAppDispatch } from "src/redux";
import { setTableConfig } from "src/redux/TableConfig/actions";
import { tTables } from "src/redux/TableConfig/types";
import { checkColumns, generateCheckColumns } from "src/util/table";

import { PdStack, PdText } from "../layout";
import { PdDivider } from "../layout/table";
import { PdCheckbox } from "../v2/forms/checkbox";

type tConfigureTablePopover<T> = {
  columns: tColumn<T>[];
  tableConfigReduxKey?: tTables;
  onPressCheckbox?: (newTableConfig: Record<string, boolean>) => void;
};

const PopoverContentTableColumns = <T extends unknown>({
  columns,
  tableConfigReduxKey,
  onPressCheckbox,
}: tConfigureTablePopover<T>): React.ReactElement => {
  const dispatch = useAppDispatch();
  const rTableConfigs = useSelector((state) =>
    tableConfigReduxKey ? state.TableConfigReducer[tableConfigReduxKey] : generateCheckColumns(columns),
  );

  const tableConfig = React.useMemo(
    () => pickBy(rTableConfigs, (_, key) => columns.map((column) => column.id).includes(key)),
    [columns, rTableConfigs],
  );
  const isAllSelected = every(tableConfig, (config) => config);

  const handleOnPressSelectAll = () => {
    const newTableConfig = checkColumns(columns);
    if (tableConfigReduxKey) dispatch(setTableConfig(tableConfigReduxKey, newTableConfig));
    onPressCheckbox?.(newTableConfig);
  };

  React.useEffect(() => {
    if (isEmpty(tableConfig) && tableConfigReduxKey) {
      dispatch(setTableConfig(tableConfigReduxKey, checkColumns(columns)));
    }
  }, [columns, dispatch, tableConfigReduxKey, tableConfig]);

  return (
    <>
      <PdText variant="body-sm" style={styles.popoverTitle}>
        Select columns to view
      </PdText>
      <PdCheckbox value={isAllSelected} onPress={handleOnPressSelectAll}>
        <PdText variant="body-sm">Select All</PdText>
      </PdCheckbox>
      <PdDivider style={styles.configureTableDivider} />
      <PdStack direction="vertical">
        {map(tableConfig, (isChecked, columnId) => {
          const column = columns.find((_column) => _column.id === columnId);
          if (column?.isHideable === false) return null;
          const toggle = () => {
            const newTableConfig = { ...tableConfig, [columnId]: !tableConfig[columnId] };
            if (tableConfigReduxKey) dispatch(setTableConfig(tableConfigReduxKey, newTableConfig));
            onPressCheckbox?.(newTableConfig);
          };
          return (
            <PdCheckbox key={columnId} value={isChecked} onPress={toggle}>
              <PdText variant="body-sm">
                {typeof column?.header === "string" ? column?.header : capitalize(column?.id)}
              </PdText>
            </PdCheckbox>
          );
        })}
      </PdStack>
    </>
  );
};

export default PopoverContentTableColumns;

const styles = StyleSheet.create({
  configureTableDivider: {
    marginVertical: kStyleConst.kSpacing * 2,
  },
  popoverTitle: {
    marginBottom: kStyleConst.kSpacing * 2,
  },
});
